import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Percent, HandCoins, DollarSign, Scale, Loader2, LogOut, Save, Settings2, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const FIELDS = [
  { key: "tax_percent", label: "Impuesto", suffix: "%", icon: Percent, desc: "Porcentaje aplicado sobre el precio del producto." },
  { key: "commission_percent", label: "Comisión", suffix: "%", icon: HandCoins, desc: "Porcentaje de comisión por el servicio de compra." },
  { key: "exchange_rate", label: "Tasa del dólar", suffix: "HNL", icon: DollarSign, desc: "Valor de 1 USD en Lempiras Hondureños." },
  { key: "price_per_pound", label: "Precio por libra", suffix: "$", icon: Scale, desc: "Costo de envío por libra en dólares." },
  { key: "price_under_pound", label: "Precio menos de 1 lb", suffix: "$", icon: Scale, desc: "Costo en dólares si el paquete pesa menos de 1 libra." },
];

export default function AdminDashboard({ user, onLogout }) {
  const [values, setValues] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [loadError, setLoadError] = useState(false);

  const loadSettings = () => {
    setLoadError(false);
    api
      .get("/settings")
      .then(({ data }) => {
        setValues(Object.fromEntries(Object.entries(data).map(([k, v]) => [k, String(v)])));
      })
      .catch(() => setLoadError(true));
  };

  useEffect(loadSettings, []);

  const validate = () => {
    const errs = {};
    for (const f of FIELDS) {
      const raw = values[f.key];
      const n = parseFloat(raw);
      if (raw === undefined || String(raw).trim() === "") errs[f.key] = "Campo requerido.";
      else if (Number.isNaN(n)) errs[f.key] = "Debe ser un número válido.";
      else if (n < 0) errs[f.key] = "No se permiten valores negativos.";
      else if (f.key === "exchange_rate" && n <= 0) errs[f.key] = "La tasa debe ser mayor a cero.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((f) => [f.key, parseFloat(values[f.key])]));
      await api.put("/settings", payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
      });
      toast.success("Configuración guardada correctamente", { id: "save-success-toast" });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast.error("Sesión expirada. Inicia sesión de nuevo.");
        onLogout();
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError)
    return (
      <div className="flex flex-col items-center gap-4 py-24" data-testid="settings-load-error">
        <p className="text-sm text-[#64748B]">No se pudo cargar la configuración.</p>
        <button
          onClick={loadSettings}
          data-testid="settings-retry-button"
          className="rounded-xl bg-[#1F2A5C] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#151E45]"
        >
          Reintentar
        </button>
      </div>
    );

  if (!values)
    return (
      <div className="flex justify-center py-24">
        <Loader2 size={28} className="animate-spin text-[#1F2A5C]" />
      </div>
    );

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-10 sm:py-14" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C9848F]">
              <Settings2 size={14} />
              Panel de administración
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-[#1E293B]">Configuración del sistema</h2>
            <p className="text-sm text-[#64748B]">Sesión iniciada como <span className="font-semibold text-[#1E293B]">{user.username}</span></p>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 text-sm font-medium text-[#64748B] transition-colors hover:border-[#C9848F] hover:text-[#1F2A5C]"
          >
            <LogOut size={16} />
            Cerrar sesión
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {FIELDS.map((f, i) => (
            <motion.div
              key={f.key}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 * i }}
              className={`rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${i === 2 ? "sm:col-span-2" : ""}`}
              data-testid={`config-card-${f.key}`}
            >
              <div className="mb-3 flex items-center gap-2.5">
                <div className="rounded-lg bg-[#1F2A5C]/10 p-2 text-[#1F2A5C]">
                  <f.icon size={17} strokeWidth={2} />
                </div>
                <h3 className="text-sm font-semibold text-[#1E293B]">{f.label}</h3>
              </div>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={f.label}
                  value={values[f.key]}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^\d*\.?\d*$/.test(v)) setValues((s) => ({ ...s, [f.key]: v }));
                  }}
                  data-testid={`config-input-${f.key}`}
                  className="w-full min-h-[52px] rounded-lg border border-[#E2E8F0] bg-[#FBF6F3] px-4 pr-14 text-lg font-semibold text-[#1E293B] outline-none transition-colors focus:ring-2 focus:ring-[#C9848F] focus:border-transparent focus:bg-white"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-[#64748B]">{f.suffix}</span>
              </div>
              {errors[f.key] && (
                <p data-testid={`config-error-${f.key}`} className="mt-2 text-xs text-red-500">
                  {errors[f.key]}
                </p>
              )}
              <p className="mt-2 text-xs text-[#64748B]">{f.desc}</p>
            </motion.div>
          ))}
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-button"
          className="mt-8 w-full min-h-[56px] rounded-xl bg-[#1F2A5C] text-base font-semibold text-white shadow-lg shadow-[#1F2A5C]/30 transition-transform transition-colors hover:bg-[#151E45] hover:-translate-y-[1px] active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={19} />}
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>

        <ChangePasswordCard onSessionExpired={onLogout} />
      </motion.div>
    </div>
  );
}

function ChangePasswordCard({ onSessionExpired }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setErrors({});
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!current.trim()) errs.current = "Ingresa tu contraseña actual.";
    if (!next.trim()) errs.next = "Ingresa la nueva contraseña.";
    else if (next.length < 6) errs.next = "Debe tener al menos 6 caracteres.";
    if (confirm !== next) errs.confirm = "Las contraseñas no coinciden.";
    if (current && next && current === next) errs.next = "Debe ser distinta a la actual.";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      await api.put(
        "/auth/password",
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` } },
      );
      toast.success("Contraseña actualizada correctamente", { id: "password-success-toast" });
      reset();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 && err.response?.data?.detail === "No autenticado") {
        toast.error("Sesión expirada. Inicia sesión de nuevo.");
        onSessionExpired();
      } else if (status === 401) {
        setErrors({ current: "Contraseña actual incorrecta." });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.15 }}
      className="mt-10 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="change-password-card"
    >
      <div className="mb-5 flex items-center gap-2.5">
        <div className="rounded-lg bg-[#1F2A5C]/10 p-2 text-[#1F2A5C]">
          <KeyRound size={17} strokeWidth={2} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-[#1E293B]">Cambiar contraseña</h3>
          <p className="text-xs text-[#64748B]">Actualiza la contraseña de tu cuenta de administrador.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="current-password" className="mb-2 block text-sm font-semibold text-[#1E293B]">
            Contraseña actual
          </label>
          <div className="relative">
            <input
              id="current-password"
              type={showCurrent ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="current-password-input"
              className="w-full min-h-[52px] rounded-lg border border-[#E2E8F0] bg-[#FBF6F3] px-4 pr-12 text-base font-medium text-[#1E293B] outline-none transition-colors focus:ring-2 focus:ring-[#C9848F] focus:border-transparent focus:bg-white"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((s) => !s)}
              data-testid="current-password-toggle-visibility"
              aria-label={showCurrent ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-[#64748B] hover:text-[#1F2A5C]"
            >
              {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.current && (
            <p data-testid="current-password-error" className="mt-2 text-xs text-red-500">
              {errors.current}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="new-password" className="mb-2 block text-sm font-semibold text-[#1E293B]">
            Nueva contraseña
          </label>
          <div className="relative">
            <input
              id="new-password"
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              data-testid="new-password-input"
              className="w-full min-h-[52px] rounded-lg border border-[#E2E8F0] bg-[#FBF6F3] px-4 pr-12 text-base font-medium text-[#1E293B] outline-none transition-colors focus:ring-2 focus:ring-[#C9848F] focus:border-transparent focus:bg-white"
            />
            <button
              type="button"
              onClick={() => setShowNext((s) => !s)}
              data-testid="new-password-toggle-visibility"
              aria-label={showNext ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-[#64748B] hover:text-[#1F2A5C]"
            >
              {showNext ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.next && (
            <p data-testid="new-password-error" className="mt-2 text-xs text-red-500">
              {errors.next}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="confirm-password" className="mb-2 block text-sm font-semibold text-[#1E293B]">
            Confirmar nueva contraseña
          </label>
          <div className="relative">
            <input
              id="confirm-password"
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="confirm-password-input"
              className="w-full min-h-[52px] rounded-lg border border-[#E2E8F0] bg-[#FBF6F3] px-4 pr-4 text-base font-medium text-[#1E293B] outline-none transition-colors focus:ring-2 focus:ring-[#C9848F] focus:border-transparent focus:bg-white"
            />
          </div>
          {errors.confirm && (
            <p data-testid="confirm-password-error" className="mt-2 text-xs text-red-500">
              {errors.confirm}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={handleSubmit}
        disabled={submitting}
        data-testid="submit-password-change-button"
        className="mt-6 w-full min-h-[52px] rounded-xl bg-[#1F2A5C] text-sm font-semibold text-white shadow-lg shadow-[#1F2A5C]/25 transition-transform transition-colors hover:bg-[#151E45] hover:-translate-y-[1px] active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2 sm:w-auto sm:px-8"
      >
        {submitting ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={17} />}
        {submitting ? "Guardando..." : "Actualizar contraseña"}
      </button>
    </motion.div>
  );
}
